import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
  Upload,
  X,
  Users,
  Search,
  Check,
} from 'lucide-react';
import { DemoPair } from '@/types';
import { listDemoPairs, createDemoPair, deleteDemoPair, updateDemoPairUsers } from '@/services/demoPairsService';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// ============================================================================
// Types
// ============================================================================

interface AdminUser {
  user_id: string;
  email: string | null;
  full_name: string | null;
}

// ============================================================================
// User Multi-Select
// ============================================================================

interface UserSelectorProps {
  users: AdminUser[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

const UserSelector: React.FC<UserSelectorProps> = ({ users, selected, onChange, disabled }) => {
  const [query, setQuery] = useState('');

  const filtered = users.filter((u) => {
    const q = query.toLowerCase();
    return (u.email ?? '').toLowerCase().includes(q) || (u.full_name ?? '').toLowerCase().includes(q);
  });

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="User suchen..."
          className="pl-9 h-8 text-sm"
          disabled={disabled}
        />
      </div>
      <div className="border border-border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Keine User gefunden</p>
        ) : (
          filtered.map((u) => {
            const isSelected = selected.includes(u.user_id);
            return (
              <button
                key={u.user_id}
                type="button"
                onClick={() => !disabled && toggle(u.user_id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50 ${
                  isSelected ? 'bg-primary/5' : ''
                }`}
                disabled={disabled}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected ? 'bg-primary border-primary' : 'border-border'
                }`}>
                  {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                </div>
                <div className="min-w-0">
                  <span className="block truncate font-medium text-foreground">{u.email ?? '—'}</span>
                  {u.full_name && (
                    <span className="block truncate text-xs text-muted-foreground">{u.full_name}</span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">{selected.length} User ausgewählt</p>
      )}
    </div>
  );
};

// ============================================================================
// Image Upload Field
// ============================================================================

interface ImageUploadFieldProps {
  label: string;
  file: File | null;
  preview: string | null;
  onFileChange: (file: File | null, preview: string | null) => void;
  disabled?: boolean;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({ label, file, preview, onFileChange, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    const url = URL.createObjectURL(f);
    onFileChange(f, url);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) handleFile(f);
  };

  const handleClear = () => {
    if (preview) URL.revokeObjectURL(preview);
    onFileChange(null, null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {preview ? (
        <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted">
          <img src={preview} alt={label} className="w-full h-full object-cover" />
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/80 border border-border flex items-center justify-center hover:bg-destructive/10 transition-colors"
            >
              <X className="w-3 h-3 text-foreground" />
            </button>
          )}
          {file && (
            <div className="absolute bottom-2 left-2 bg-background/80 rounded px-2 py-0.5 text-xs text-muted-foreground truncate max-w-[80%]">
              {file.name}
            </div>
          )}
        </div>
      ) : (
        <div
          className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
        >
          <Upload className="w-6 h-6 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Bild hochladen</span>
          <span className="text-xs text-muted-foreground/60">oder hierher ziehen</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
      />
    </div>
  );
};

// ============================================================================
// Edit Users Dialog
// ============================================================================

interface EditUsersDialogProps {
  pair: DemoPair;
  users: AdminUser[];
  onClose: () => void;
  onSaved: (updatedIds: string[]) => void;
}

const EditUsersDialog: React.FC<EditUsersDialogProps> = ({ pair, users, onClose, onSaved }) => {
  const [selected, setSelected] = useState<string[]>(pair.userIds);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updateDemoPairUsers(pair.id, selected);
      onSaved(selected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>User für Demo-Paar</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Nur für die ausgewählten User wird das Demo-Bild anstelle der KI-Generierung verwendet.
          </p>
          <UserSelector users={users} selected={selected} onChange={setSelected} disabled={isSaving} />
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Add Pair Dialog
// ============================================================================

interface AddPairDialogProps {
  users: AdminUser[];
  onClose: () => void;
  onSaved: () => void;
}

const AddPairDialog: React.FC<AddPairDialogProps> = ({ users, onClose, onSaved }) => {
  const [inputFile, setInputFile] = useState<File | null>(null);
  const [inputPreview, setInputPreview] = useState<string | null>(null);
  const [outputFile, setOutputFile] = useState<File | null>(null);
  const [outputPreview, setOutputPreview] = useState<string | null>(null);
  const [method, setMethod] = useState<'empty' | 'staged'>('empty');
  const [label, setLabel] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!inputFile || !outputFile) {
      setError('Bitte lade sowohl ein Eingabe- als auch ein Ausgabebild hoch.');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('Bitte wähle mindestens einen User aus.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await createDemoPair(inputFile, outputFile, method, label, selectedUsers);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Scrolling lives in an inner child: making the transformed (translate-centered),
          rounded DialogContent itself the scroller causes white compositing artifacts
          at the edges during fast momentum scrolls (same pattern as UpdateDetailModal). */}
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>Neues Demo-Paar hinzufügen</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-6 py-2 space-y-5">
          {/* Method selector */}
          <div className="space-y-2">
            <Label>Methode</Label>
            <div className="flex gap-2">
              {(['empty', 'staged'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    method === m
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-muted-foreground/50'
                  }`}
                >
                  {m === 'empty' ? 'Leeren' : 'Staging'}
                </button>
              ))}
            </div>
          </div>

          {/* Image uploads */}
          <div className="grid grid-cols-2 gap-4">
            <ImageUploadField
              label="Eingabebild (Original)"
              file={inputFile}
              preview={inputPreview}
              onFileChange={(f, p) => { setInputFile(f); setInputPreview(p); }}
              disabled={isSaving}
            />
            <ImageUploadField
              label="Ausgabebild (Ergebnis)"
              file={outputFile}
              preview={outputPreview}
              onFileChange={(f, p) => { setOutputFile(f); setOutputPreview(p); }}
              disabled={isSaving}
            />
          </div>

          {/* Optional label */}
          <div className="space-y-2">
            <Label>Bezeichnung (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Wohnzimmer Demo"
              disabled={isSaving}
            />
          </div>

          {/* User selection */}
          <div className="space-y-2">
            <Label>User (für wen gilt dieses Paar?)</Label>
            <UserSelector users={users} selected={selectedUsers} onChange={setSelectedUsers} disabled={isSaving} />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 px-6 pb-6 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={isSaving || !inputFile || !outputFile}>
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird gespeichert...</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" />Paar hinzufügen</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Main View
// ============================================================================

export const AdminDemoPairsView: React.FC = () => {
  const [pairs, setPairs] = useState<DemoPair[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingUsersPair, setEditingUsersPair] = useState<DemoPair | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [pairsData, usersData] = await Promise.all([
        listDemoPairs(),
        supabase.rpc('get_admin_user_stats', { p_limit: 1000, p_offset: 0 }),
      ]);
      setPairs(pairsData);
      setUsers(
        (usersData.data ?? []).map((u: AdminUser) => ({
          user_id: u.user_id,
          email: u.email,
          full_name: u.full_name,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteDemoPair(id);
      setPairs((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUsersSaved = (pairId: string, updatedIds: string[]) => {
    setPairs((prev) => prev.map((p) => p.id === pairId ? { ...p, userIds: updatedIds } : p));
    setEditingUsersPair(null);
  };

  const getUserLabel = (userId: string) => {
    const u = users.find((u) => u.user_id === userId);
    return u?.email ?? userId.slice(0, 8) + '…';
  };

  return (
    <div className="space-y-6">
      {showAddDialog && (
        <AddPairDialog
          users={users}
          onClose={() => setShowAddDialog(false)}
          onSaved={() => { setShowAddDialog(false); loadData(); }}
        />
      )}

      {editingUsersPair && (
        <EditUsersDialog
          pair={editingUsersPair}
          users={users}
          onClose={() => setEditingUsersPair(null)}
          onSaved={(ids) => handleUsersSaved(editingUsersPair.id, ids)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Demo-Paare</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Vordefinierte Eingabe→Ausgabe-Bildpaare. Die KI-Generierung wird nur für zugewiesene User übersprungen.
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Neues Paar
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : pairs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Upload className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Noch keine Demo-Paare vorhanden.</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Füge ein Paar hinzu, um die Demo-Funktion zu aktivieren.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {pairs.map((pair) => (
            <Card key={pair.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Thumbnails */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-24 h-16 rounded-lg overflow-hidden bg-muted border border-border">
                      <img
                        src={pair.inputUrl}
                        alt="Input"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                    <span className="text-muted-foreground/60 text-lg">→</span>
                    <div className="w-24 h-16 rounded-lg overflow-hidden bg-muted border border-border">
                      <img
                        src={pair.outputUrl}
                        alt="Output"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          pair.method === 'empty'
                            ? 'bg-blue-500/10 text-blue-500'
                            : 'bg-emerald-500/10 text-emerald-500'
                        }`}
                      >
                        {pair.method === 'empty' ? 'Leeren' : 'Staging'}
                      </span>
                      {pair.label && (
                        <span className="text-sm font-medium text-foreground">{pair.label}</span>
                      )}
                    </div>

                    {/* Assigned users */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {pair.userIds.length === 0 ? (
                        <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">
                          Keine User — inaktiv
                        </span>
                      ) : (
                        pair.userIds.map((uid) => (
                          <span
                            key={uid}
                            className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full truncate max-w-[180px]"
                          >
                            {getUserLabel(uid)}
                          </span>
                        ))
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground font-mono">
                      Hash: {pair.inputPhash}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingUsersPair(pair)}
                      className="text-muted-foreground hover:text-foreground"
                      title="User verwalten"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(pair.id)}
                      disabled={deletingId === pair.id}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      {deletingId === pair.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
